import { useState, useEffect, useCallback } from "react";
import VisibilityIcon from '@mui/icons-material/Visibility';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import InputAdornment from '@mui/material/InputAdornment';
import { Grid, TextField, Button, MenuItem, Typography } from "@mui/material";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CircularProgress from '@mui/material/CircularProgress';
import { Form as FormikForm, useFormikContext } from "formik";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

import { CustomField } from '../../../inputs/CustomField';
import { useAxios } from "../../../../context/AxiosHookContext";
import getDayjs from "../../../../context/DateInstance";

export const TicketForm = ({ onClose }) => {

    const axios = useAxios();
    const [list, setList] = useState([])
    const [departments, setDepartments] = useState([])
    const [filesList, setFileList] = useState([])
    const [filesInput, setInputValue] = useState(null)
    const [loadingFile, setLoadingFile] = useState(false)

    const { values, setFieldValue, isSubmitting } = useFormikContext();

    const onSelectItems = async (event) => {
        try {
            setLoadingFile(true);
            const files = event?.target.files || []

            if (files.length > 0 && values._id) {
                for (const key in Object.keys(files)) {
                    const file = files[key];
                    const form = new FormData();
                    form.append('file', file);
                    await axios.post(`/tickets/uploadDocument/${values._id}`, form)
                    getTicketFiles();
                }
            } else {
                setFieldValue("files", files)
            }
        } catch (error) {
            console.log(event)
        } finally {
            setInputValue(null)
            setLoadingFile(false);
        }
    }

    const removeTicketFile = async (file) => {
        try {
            await axios.delete(`/tickets/delete_ticket_file/${file._id}`);
        } catch (error) {
            console.log(error)
        } finally {
            getTicketFiles();
        }
    }

    const getTicketFiles = useCallback(async () => {
        try {
            const { data } = await axios.get(`/tickets/get_ticket_files/${values._id}`);
            setFileList(data.data)
        } catch (error) {
            console.log(error)
        }
    }, [axios, setFileList])

    const loadCategories = useCallback(async (departmentShortName) => {
        try {
            const types = JSON.stringify(["support_type", "support_subtype", "support_subtype_1"])
            const { data } = await axios.get(`/tickets/category?page=1&limit=50000&types=${types}&active=true&departmentShortName=${departmentShortName}`);
            setList(data.data.list)
        } catch (error) {
            console.log(error)
        }
    }, [axios])

    useEffect(() => {
        const init = async () => {
            try {
                const { data } = await axios.get('/users/ticketDepartments');
                let depts = data?.data || []

                // Si es edicion y el departamento del ticket no esta en la lista, agregarlo
                if (values.departmentShortName && !depts.find(d => d.departmentShortName === values.departmentShortName)) {
                    depts = [...depts, { departmentShortName: values.departmentShortName, department: values.department || '' }]
                }

                setDepartments(depts)

                if (values.departmentShortName) {
                    await loadCategories(values.departmentShortName)
                }
            } catch (error) {
                console.log(error)
            }
        }

        if (values._id) {
            getTicketFiles();
        }

        init();
        setFieldValue("eventDate", getDayjs(values?.eventDate || getDayjs()))
    }, [getTicketFiles])

    const handleDepartmentChange = async (e) => {
        const { value } = e.target;
        const dept = departments.find(d => d.departmentShortName === value);
        setFieldValue("departmentShortName", value)
        setFieldValue("department", dept?.department || "")
        setFieldValue("support_type", "")
        setFieldValue("support_subtype", "")
        setFieldValue("support_subtype_1", "")
        setList([])
        await loadCategories(value)
    }

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "support_type") {
            setFieldValue(name, value)
            setFieldValue("support_subtype", "")
            setFieldValue("support_subtype_1", "")
        }
        if (name === "support_subtype") {
            setFieldValue(name, value)
            setFieldValue("support_subtype_1", "")
        }
        if (name === "support_subtype_1") {
            setFieldValue(name, value)
        }
    }

    return (
        <FormikForm>
            {departments.length > 0 && (
                <Grid container spacing={2}>

                    <Grid item xs={12}>
                        <CustomField
                            name="departmentShortName"
                            label="Departamento"
                            select
                            fullWidth
                            as={TextField}
                            disabled={!!values?._id || values?.status === "Finished"}
                            onChange={handleDepartmentChange}
                        >
                            {departments.map((dept) => (
                                <MenuItem value={dept.departmentShortName} key={dept.departmentShortName}>
                                    {dept.department ? `${dept.department} (${dept.departmentShortName})` : dept.departmentShortName}
                                </MenuItem>
                            ))}
                        </CustomField>
                    </Grid>

                    {list.length > 0 && (
                        <>
                            <Grid item xs={12} sm={12} md={6} lg={6}>
                                <CustomField name="support_type" label="Seleccione el tipo de soporte" select fullWidth as={TextField} disabled={values?.status === "Finished"}
                                    onChange={handleChange}
                                >
                                    {list?.filter(item => item?.type === "support_type")?.map((category) => (
                                        <MenuItem value={category._id} key={category._id}>{category.displayName}</MenuItem>
                                    ))}
                                </CustomField>
                            </Grid>

                            <Grid item xs={12} sm={12} md={6} lg={6}>
                                <CustomField name="support_subtype" label="Especifique el subtipo" select fullWidth as={TextField} disabled={values?.status === "Finished"}
                                    onChange={handleChange}
                                >
                                    {list?.filter(item => item?.type === "support_subtype" && item.father === values?.support_type)?.map((category) => (
                                        <MenuItem value={category._id} key={category._id}>{category.displayName}</MenuItem>
                                    ))}
                                </CustomField>
                            </Grid>

                            <Grid item xs={12} sm={12} md={6} lg={6}>
                                <CustomField name="support_subtype_1" label="Especifique su necesidad" select fullWidth as={TextField} disabled={values?.status === "Finished"}
                                    onChange={handleChange}
                                >
                                    {list?.filter(item => item?.type === "support_subtype_1" && item.father === values?.support_subtype)?.map((category) => (
                                        <MenuItem value={category._id} key={category._id}>{category.displayName}</MenuItem>
                                    ))}
                                </CustomField>
                            </Grid>

                            <Grid item xs={12} sm={12} md={6} lg={6}>
                                <LocalizationProvider dateAdapter={AdapterDayjs} fullWidth>
                                    <CustomField name="eventDate" label="Fecha del evento" fullWidth as={DateTimePicker} disabled={values?.status === "Finished"}
                                        onChange={(e) => { console.log(e) }}
                                        sx={{
                                            width: "100%",
                                            '& .MuiInputBase-input': {
                                                fontSize: '15px',
                                                padding: '8.5px 32px 8.5px 14px',
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>

                            <Grid item xs={12}>
                                <CustomField name="comment" multiline rows={5} label="Por favor describa la solicitud." fullWidth as={TextField} disabled={values?.status === "Finished"} />
                            </Grid>

                            {!["Finished"].includes(values?.status || null) && (
                                <Grid item xs={12}>
                                    <label
                                        style={{ display: "flex", height: 40, width: "100% !important", backgroundColor: "#e5e0e0", borderRadius: 7, justifyContent: "center", alignItems: "center", cursor: "pointer" }}
                                        htmlFor="input-load-file">
                                        {loadingFile ? <CircularProgress size={"20px"} sx={{ color: "#4f0a5270" }} /> : <CloudUploadIcon sx={{ fontSize: 40, color: "#4f0a5270" }} />}
                                    </label>
                                    <input
                                        onChange={onSelectItems}
                                        value={filesInput}
                                        type="file"
                                        style={{ display: "none" }}
                                        id="input-load-file"
                                        multiple
                                        accept=".png, .jpeg, .jpg, .pdf, .xls, .xlsx, .zip, .rar"
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                {filesList?.map((file, key) => (
                                    <TextField
                                        key={key}
                                        sx={{ margin: 0.3 }}
                                        fullWidth
                                        disabled
                                        value={file.file_name}
                                        InputProps={{
                                            style: { height: 40 },
                                            startAdornment: (
                                                <InputAdornment position="start" onClick={() => {
                                                    window.open(process.env.REACT_APP_API_FULL_DOMAIN + file.file_api_path, '_blank')
                                                }} sx={{ cursor: "pointer" }}>
                                                    <VisibilityIcon sx={{ fontSize: 40 }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: ["Assigned", "Finished"].includes(values?.status || null) ? (<></>) : (
                                                <InputAdornment position="end" onClick={() => removeTicketFile(file)}>
                                                    <HighlightOffIcon sx={{ fontSize: 40, cursor: "pointer" }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                ))}
                            </Grid>

                            <Grid item xs={12}>
                                {values?.status === "Finished" && (
                                    <Typography variant="h6">Esta solicitud se encuentra en estado finalizada.</Typography>
                                )}
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12} textAlign={"right"} mt={3}>
                        <Button
                            variant="outlined"
                            onClick={() => onClose()}
                            disabled={!((values && !values?.status !== "Created") || false)}
                        >
                            cancelar
                        </Button>
                        <Button sx={{ marginLeft: 2 }}
                            variant="contained"
                            type="submit"
                            disabled={isSubmitting || values?.status === "Finished"}
                        >
                            {isSubmitting && <CircularProgress size={"15px"} sx={{ color: "white", marginRight: "6px" }} />}
                            {isSubmitting ? 'Guardando' : 'Guardar'}
                        </Button>
                    </Grid>

                </Grid>
            )}
        </FormikForm>
    );
}

export default TicketForm;
